/**
 * "What to do about it" — the fourth thing the brief asks a finding card to
 * carry, next to the rule ID, the § source and the computed values.
 *
 * The engine does not ship an action text (its `detail`/`note_de` never crosses
 * the API boundary), so the sentence is derived from the SHAPE of the
 * machine-readable `computed` block plus the severity. That shape is the
 * engine's own vocabulary, one per check type:
 *
 *   { artifacts, missing }                        required document(s)
 *   { fields, missing }                           form fields must be numeric
 *   { form_field, form_value, gis_layer, … }      form ↔ GIS consistency
 *   { gis_layer, gis_value }                      a GIS flag
 *   { parameter, value, operator, limit }         a threshold comparison
 *
 * Nothing here reads the German message string, and nothing here re-decides
 * anything: the severity is the engine's, the numbers are the engine's, and the
 * sentence only says what a human does next. No sentence may say "approved",
 * "ready to submit" or anything of that family — a test greps for it.
 */
import type { ComputedValues, Severity } from '@/api/types'
import type { TranslationKey } from '@/i18n'

export interface FindingAction {
  key: TranslationKey
  /** Interpolated into `{items}` where the sentence names concrete values. */
  items?: string
}

function list(values: readonly string[] | null | undefined): string | null {
  if (!values || values.length === 0) return null
  return values.join(', ')
}

/**
 * The action for a HARD finding.
 *
 * `severity` decides only between "nothing to do" (green) and "here is the
 * move"; which move it is comes from the computed shape.
 */
export function actionForFinding(
  severity: Severity,
  computed: ComputedValues | null | undefined,
): FindingAction {
  if (severity === 'green') return { key: 'action.green' }

  const missing = list(computed?.missing)

  // A required document is missing — the engine listed exactly which.
  if (computed?.artifacts && missing) {
    return { key: 'action.missingArtifacts', items: missing }
  }

  // A form field is empty or not numeric — again, named by the engine.
  if (computed?.fields && missing) {
    return { key: 'action.missingFormFields', items: missing }
  }

  // Form and GIS disagree.
  if (computed?.form_field && computed?.gis_layer) {
    return { key: 'action.inconsistent' }
  }

  // A GIS flag stands.
  if (computed?.gis_layer) {
    return { key: 'action.gisFlag' }
  }

  // A threshold comparison did not hold.
  if (computed?.parameter && computed?.operator) {
    return { key: 'action.limitNotMet' }
  }

  // Anything the shapes above do not cover: say the honest generic thing
  // rather than nothing. A card without an action is a card that stops halfway.
  return { key: 'action.generic' }
}

/**
 * The action for INDICATIVE output. Deliberately one sentence for every case:
 * the rule or the zone is not verified, so the only correct next step is to
 * read the cited provision itself. There is no severity here to react to —
 * `IndicativeFinding` does not have one, by design.
 */
export function actionForIndicative(): FindingAction {
  return { key: 'action.indicative' }
}
